import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';

import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MachineHistoryActions } from 'redux/actions/machines/MachineHistoryActions';
import { MachineActions } from 'redux/actions/settings/MachineActions';
import { MachineMaintenanceActions } from 'redux/actions/settings/MachineMaintenanceActions';

import { PageContentHeader } from 'app/components/common/PageContentHeader';
import { Table, TableColumn, TableRow } from 'app/components/common/Table';
import Translator from 'app/components/common/Translator';
import { LoginUser } from 'models/LoginUser';
import { Machine } from 'models/Machine';
import { MachineHistory } from 'models/MachineHistory';
import { MachineMaintenance } from 'models/MachineMaintenance';
import { StoreModel } from 'models/StoreModel';
import { Translations } from 'models/Translations';
import { newestFirst } from 'app/pages/Dashboard/DailyReportCard';

interface Props {
    fetchMachineMaintenances: typeof MachineMaintenanceActions.fetchMachineMaintenances;
    fetchMachines: typeof MachineActions.fetchMachines;
    fetchMachineHistories: typeof MachineHistoryActions.fetchMachineHistories;
    loginUser: LoginUser;
    machineMaintenances: MachineMaintenance[];
    machines: Machine[];
    machineHistories: MachineHistory[];
    translations: Translations;
}

interface SortTableRow extends TableRow {
    sortval: number
}

class UpcomingMaintenance extends React.Component<Props> {
    componentDidMount() {
        if (this.loggedIn()) {
            if (!this.props.machineMaintenances) {
                this.props.fetchMachineMaintenances(this.props.loginUser.token);
            }
            if (!this.props.machines) {
                this.props.fetchMachines(this.props.loginUser.token);
            }
            if (!this.props.machineHistories) {
                this.props.fetchMachineHistories(this.props.loginUser.token);
            }
        }
    }

    loggedIn() {
        return this.props.loginUser && this.props.loginUser.token;
    }

    ready() {
        return this.loggedIn() && this.props.machineMaintenances && this.props.machines && this.props.machineHistories
    }

    setTableData(): TableRow[] {
        let sortData: SortTableRow[] = [];
        let data: TableRow[] = [];
        // one row per machine, several machines can share a machine type
        this.props.machines.forEach(machine => this.props.machineMaintenances.filter(mm => mm.machineType.id === machine.machineType.id).forEach(machineMaintenance => {
            const lastMh = this.getLastMachineHistory(machine, machineMaintenance);
            const intervalDays = this.computeRemainingDays(machineMaintenance, lastMh);
            const intervalServiceLife = this.computeRemainingSl(machineMaintenance, machine, lastMh);
            sortData.push({
                dataObject: {
                    id: machineMaintenance.id,
                    machine: machine,
                    maintenance: machineMaintenance.maintenance,
                    intervalDays: intervalDays,
                    intervalServiceLife: intervalServiceLife,
                    archivedDate: machineMaintenance.archivedDate ?? machine.archivedDate
                },
                cells: [
                    {
                        name: "intervalDays",
                        cell: this.displayRemainingDays(intervalDays, machineMaintenance.warningThresholdDays),
                        sortVal: intervalDays.toString()
                    },
                    {
                        name: "intervalServiceLife",
                        cell: this.displayRemainingSl(intervalServiceLife, machineMaintenance.warningThresholdServiceLife),
                        sortVal: intervalServiceLife.toString()
                    },
                ],
                // values can be "" or "N/A", compared like before through JS coercion
                sortval: ((intervalDays as number) < 0 || (intervalServiceLife as number) < 0) ? 2 : (((intervalDays as number) < machineMaintenance.warningThresholdDays || (intervalServiceLife as number) < machineMaintenance.warningThresholdServiceLife) ? 1 : 0)
            })
        }))
        data = sortData.sort(this.sortTd)
        return data;
    }

    sortTd(a: SortTableRow, b: SortTableRow) {
        if (a.sortval > b.sortval)
            return -1;
        else
            if (a.sortval < b.sortval)
                return 1;
            else
                return 0;
    }

    computeRemainingDays(mM: MachineMaintenance, lastMh?: MachineHistory) {
        if (mM.intervalDays <= 0) {
            return "";
        }
        const oneDay = 24 * 60 * 60 * 1000;
        if (lastMh) {
            return Math.round(mM.intervalDays - Math.abs((new Date().getTime() - new Date(lastMh.date).getTime()) / oneDay))
        } else {
            return "N/A"
        }
    }

    displayRemainingDays(rd: number | string, rdT: number) {
        const isOverdue = rd !== undefined && typeof rd === "number" && rd < 0;
        const isWarning = rd !== undefined && typeof rd === "number" && rd <= rdT;
        return (
            <>
                {isOverdue &&
                    <FontAwesomeIcon icon={faExclamationTriangle} className="c-table__cell--icon c-table__cell--icon-error"></FontAwesomeIcon>
                }
                {!isOverdue && isWarning &&
                    <FontAwesomeIcon icon={faExclamationTriangle} className="c-table__cell--icon c-table__cell--icon-warning"></FontAwesomeIcon>
                }
                {rd}
            </>
        )
    }

    displayRemainingSl(sl: number | string, slT: number) {
        const isOverdue = typeof sl === "number" && sl < 0;
        const isWarning = typeof sl === "number" && sl <= slT;
        return (
            <>
                {isOverdue &&
                    <FontAwesomeIcon icon={faExclamationTriangle} className="c-table__cell--icon c-table__cell--icon-error"></FontAwesomeIcon>
                }
                {!isOverdue && isWarning &&
                    <FontAwesomeIcon icon={faExclamationTriangle} className="c-table__cell--icon c-table__cell--icon-warning"></FontAwesomeIcon>
                }
                {sl}
            </>
        )
    }

    computeRemainingSl(mM: MachineMaintenance, machine: Machine, lastMh?: MachineHistory) {
        if (mM.intervalServiceLife <= 0) {
            return "";
        }
        if (lastMh) {
            return mM.intervalServiceLife - (machine.serviceLife - lastMh.serviceLife)
        } else {
            return "N/A"
        }
    }

    getLastMachineHistory(machine: Machine, mM: MachineMaintenance) {
        return this.props.machineHistories.filter(mH => mH.machine.id === machine.id && !mH.archivedDate && mH.machineMaintenances?.some(mm => mm.id === mM.id)).sort(newestFirst)[0];
    }

    setColumns(): TableColumn[] {
        // TODO maybe dynamic??
        return [
            {
                header: 'Id',
                accessor: 'id',
            },
            {
                header: 'Machine',
                accessor: 'machine',
            },
            {
                header: 'Maintenance',
                accessor: 'maintenance',
            },
            {
                header: 'Remaining days',
                accessor: 'intervalDays',
            },
            {
                header: 'Remaining service life',
                accessor: 'intervalServiceLife',
            },
        ];
    }

    render() {
        if (!this.ready()) {
            return (
                <div className="c-page__content">
                    <PageContentHeader text="Upcoming Maintenance" />
                    <Translator text="Loading" />
                </div>
            )
        }
        return (
            <div className="c-page__content">
                <PageContentHeader text="Upcoming Maintenance" />
                <div className="c-card">
                    <Table
                        editable={false}
                        translations={this.props.translations}
                        columns={this.setColumns()}
                        tableData={this.setTableData()}
                        loginUser={this.props.loginUser}
                    />
                </div>
            </div>
        );
    }
}

function mapStateToProps(state: StoreModel) {
    return {
        loginUser: state.loginUser,
        machines: state.machines,
        machineMaintenances: state.machineMaintenances,
        machineHistories: state.machineHistories,
        translations: state.translations
    };
}
function mapDispatchToProps(dispatch: Dispatch) {
    return {
        fetchMachineMaintenances: bindActionCreators(MachineMaintenanceActions.fetchMachineMaintenances, dispatch),
        fetchMachines: bindActionCreators(MachineActions.fetchMachines, dispatch),
        fetchMachineHistories: bindActionCreators(MachineHistoryActions.fetchMachineHistories, dispatch),
    };
}
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(UpcomingMaintenance);